import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";

interface Props {
  clubId?: string;
  readingListMode?: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export function BookSearchDialog({ clubId, readingListMode, onClose, onAdded }: Props) {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const { t } = useTranslation();

  const { data: results, isLoading, error } = trpc.books.search.useQuery(
    { query: submitted },
    { enabled: submitted.length > 0 }
  );

  const [pendingWorkId, setPendingWorkId] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const addToBucket = trpc.books.addToBucketList.useMutation({
    onMutate: async (vars) => {
      if (!clubId) return;
      await utils.clubBooks.list.cancel({ clubId });
      const prev = utils.clubBooks.list.getData({ clubId });
      utils.clubBooks.list.setData({ clubId }, (old) => {
        const item = {
          id: `optimistic-${vars.workId}`,
          state: "BUCKET" as const,
          canonicalPageCount: null,
          myPriority: null,
          priorityCount: 0,
          addedAt: new Date().toISOString(),
          book: { title: vars.title, authors: vars.authors, coverUrl: vars.coverUrl ?? null },
        };
        return [...(old ?? []), item] as typeof old;
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (clubId && ctx?.prev) utils.clubBooks.list.setData({ clubId }, ctx.prev);
      setPendingWorkId(null);
    },
    onSuccess: () => {
      if (clubId) utils.clubBooks.list.invalidate({ clubId });
      setPendingWorkId(null);
      onAdded();
    },
  });
  const addToList = trpc.books.addToReadingList.useMutation({
    onMutate: async (vars) => {
      await utils.readingList.list.cancel();
      const prev = utils.readingList.list.getData({});
      utils.readingList.list.setData({}, (old) => {
        const item = {
          id: `optimistic-${vars.workId}`,
          bookId: `optimistic-book-${vars.workId}`,
          status: "WANT_TO_READ" as const,
          rating: null,
          notes: null,
          addedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          book: { id: `optimistic-book-${vars.workId}`, title: vars.title, authors: vars.authors, coverUrl: vars.coverUrl ?? null },
        };
        return [item, ...(old ?? [])] as typeof old;
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.readingList.list.setData({}, ctx.prev);
      setPendingWorkId(null);
    },
    onSuccess: () => {
      utils.readingList.list.invalidate();
      setPendingWorkId(null);
      onAdded();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "oklch(17% 0.025 55 / 0.5)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-2xl flex flex-col scrollbar-thin"
        style={{
          maxWidth: "calc(100vw - 32px)",
          background: "var(--color-card)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-xl)",
          maxHeight: "80vh",
          animation: "slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: "1px solid var(--color-border)" }}
        >
          <h2
            className="font-serif font-semibold text-base"
            style={{ color: "var(--color-foreground)", letterSpacing: "-0.01em" }}
          >
            {readingListMode ? t("bookSearch.titleReadingList") : t("bookSearch.titleBucket")}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md text-lg leading-none px-2 py-0.5 transition-colors"
            style={{ color: "var(--color-muted-foreground)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-foreground)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-muted-foreground)")}
          >
            ×
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--color-border)" }}>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (query.trim()) setSubmitted(query.trim());
            }}
          >
            <Input
              placeholder={t("bookSearch.searchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" loading={isLoading}>{t("bookSearch.searchButton")}</Button>
          </form>
          {error && (
            <p className="mt-2 text-xs" style={{ color: "var(--color-destructive)" }}>
              {error.message}
            </p>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {(addToBucket.error || addToList.error) && (
            <div
              className="px-5 py-2 text-xs"
              style={{
                background: "oklch(95% 0.03 25)",
                color: "var(--color-destructive)",
                borderBottom: "1px solid var(--color-border)",
              }}
            >
              {(addToBucket.error ?? addToList.error)?.message}
            </div>
          )}
          {submitted && results?.length === 0 && (
            <p className="px-5 py-6 text-sm italic" style={{ color: "var(--color-muted-foreground)" }}>
              {t("bookSearch.noResults")}
            </p>
          )}
          {results?.map((book) => (
            <div
              key={book.workId}
              className="flex items-start gap-3 px-5 py-3 transition-colors"
              style={{ borderBottom: "1px solid var(--color-border)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-muted)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            >
              {book.coverUrl ? (
                <img
                  src={book.coverUrl}
                  alt=""
                  className="w-10 h-14 object-cover rounded flex-shrink-0"
                  style={{ background: "var(--color-muted)" }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              ) : (
                <div className="w-10 h-14 rounded flex-shrink-0" style={{ background: "var(--color-muted)" }} />
              )}
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-medium truncate font-serif"
                  style={{ color: "var(--color-foreground)" }}
                >
                  {book.title}
                </p>
                <p className="text-xs truncate" style={{ color: "var(--color-muted-foreground)" }}>
                  {book.authors.join(", ")}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                loading={pendingWorkId === book.workId}
                disabled={pendingWorkId !== null}
                onClick={() => {
                  const bookPayload = {
                    workId: book.workId,
                    title: book.title,
                    authors: book.authors,
                    coverUrl: book.coverUrl,
                    isbn: book.isbn,
                    pageCount: book.pageCount,
                    rawPayload: book as Record<string, unknown>,
                  };
                  setPendingWorkId(book.workId);
                  if (readingListMode) {
                    addToList.mutate(bookPayload);
                  } else {
                    addToBucket.mutate({ clubId: clubId!, ...bookPayload });
                  }
                }}
              >
                {t("bookSearch.addButton")}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
